/**
 * API client for communicating with the Gallery Fotoatelier backend.
 * All requests go through this module for consistent error handling
 * and admin authentication.
 */

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  isAdmin = false,
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  // Sent on every request, not just the authenticated ones: public endpoints use it to
  // decide whether this visitor may see shop prices while the shop is closed.
  const token = localStorage.getItem('gallery-auth-token');
  if (token) headers['Authorization'] = `Bearer ${token}`;

  if (isAdmin) {
    const adminKey = localStorage.getItem('gallery-admin-key');
    if (adminKey) headers['X-Admin-Key'] = adminKey;
  }

  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { code: 'UNKNOWN', message: res.statusText } }));
    throw new ApiError(res.status, err.error?.code || 'UNKNOWN', err.error?.message || res.statusText);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  const json: ApiResponse<T> = await res.json();
  return json.data;
}

// Public endpoints (no auth)
export const publicApi = {
  getArtists: () => request<unknown[]>('GET', '/artists'),
  getArtworks: () => request<unknown[]>('GET', '/artworks'),
  getArtworksWithImages: () => request<unknown[]>('GET', '/artworks?images=true'),
  getExhibitions: () => request<unknown[]>('GET', '/exhibitions'),
  getArtFairs: () => request<unknown[]>('GET', '/art-fairs'),
  getTeam: () => request<unknown[]>('GET', '/team'),
  getCategories: () => request<unknown[]>('GET', '/categories'),
  getDropshop: () => request<unknown[]>('GET', '/dropshop'),
  getNews: () => request<unknown[]>('GET', '/news'),
  getSettings: () => request<unknown>('GET', '/settings'),
  getFAQ: () => request<unknown[]>('GET', '/faq'),
  getPartners: () => request<unknown[]>('GET', '/partners'),
};

// Admin endpoints (require X-Admin-Key)
export const adminApi = {
  // Artists
  getArtists: () => request<unknown[]>('GET', '/admin/artists', undefined, true),
  createArtist: (data: unknown) => request<unknown>('POST', '/admin/artists', data, true),
  updateArtist: (id: string, data: unknown) => request<unknown>('PUT', `/admin/artists/${id}`, data, true),
  deleteArtist: (id: string) => request<unknown>('DELETE', `/admin/artists/${id}`, undefined, true),

  // Artworks
  getArtworks: () => request<unknown[]>('GET', '/admin/artworks', undefined, true),
  createArtwork: (data: unknown) => request<unknown>('POST', '/admin/artworks', data, true),
  updateArtwork: (id: string, data: unknown) => request<unknown>('PUT', `/admin/artworks/${id}`, data, true),
  deleteArtwork: (id: string) => request<unknown>('DELETE', `/admin/artworks/${id}`, undefined, true),

  // Exhibitions
  getExhibitions: () => request<unknown[]>('GET', '/admin/exhibitions', undefined, true),
  createExhibition: (data: unknown) => request<unknown>('POST', '/admin/exhibitions', data, true),
  updateExhibition: (id: string, data: unknown) => request<unknown>('PUT', `/admin/exhibitions/${id}`, data, true),
  deleteExhibition: (id: string) => request<unknown>('DELETE', `/admin/exhibitions/${id}`, undefined, true),

  // Art Fairs
  getArtFairs: () => request<unknown[]>('GET', '/admin/art-fairs', undefined, true),
  createArtFair: (data: unknown) => request<unknown>('POST', '/admin/art-fairs', data, true),
  updateArtFair: (id: string, data: unknown) => request<unknown>('PUT', `/admin/art-fairs/${id}`, data, true),
  deleteArtFair: (id: string) => request<unknown>('DELETE', `/admin/art-fairs/${id}`, undefined, true),

  // Team
  getTeam: () => request<unknown[]>('GET', '/admin/team', undefined, true),
  createTeamMember: (data: unknown) => request<unknown>('POST', '/admin/team', data, true),
  updateTeamMember: (id: string, data: unknown) => request<unknown>('PUT', `/admin/team/${id}`, data, true),
  deleteTeamMember: (id: string) => request<unknown>('DELETE', `/admin/team/${id}`, undefined, true),
  reorderTeam: (orderedIds: string[]) => request<unknown[]>('PUT', '/admin/team/reorder', { orderedIds }, true),

  // Categories
  getCategories: () => request<unknown[]>('GET', '/admin/categories', undefined, true),
  createCategory: (data: unknown) => request<unknown>('POST', '/admin/categories', data, true),
  updateCategory: (id: string, data: unknown) => request<unknown>('PUT', `/admin/categories/${id}`, data, true),
  deleteCategory: (id: string) => request<unknown>('DELETE', `/admin/categories/${id}`, undefined, true),
  reorderCategories: (orderedIds: string[]) => request<unknown[]>('PUT', '/admin/categories/reorder', { orderedIds }, true),
  addSubcategory: (catId: string, data: unknown) => request<unknown>('POST', `/admin/categories/${catId}/subcategories`, data, true),
  updateSubcategory: (catId: string, subId: string, data: unknown) => request<unknown>('PUT', `/admin/categories/${catId}/subcategories/${subId}`, data, true),
  deleteSubcategory: (catId: string, subId: string) => request<unknown>('DELETE', `/admin/categories/${catId}/subcategories/${subId}`, undefined, true),

  // Dropshop
  getDropshop: () => request<unknown[]>('GET', '/admin/dropshop', undefined, true),
  createDropshop: (data: unknown) => request<unknown>('POST', '/admin/dropshop', data, true),
  updateDropshop: (id: string, data: unknown) => request<unknown>('PUT', `/admin/dropshop/${id}`, data, true),
  deleteDropshop: (id: string) => request<unknown>('DELETE', `/admin/dropshop/${id}`, undefined, true),

  // News
  verifyKey: () => request<{ ok: boolean }>('GET', '/admin/verify', undefined, true),
  getNews: () => request<unknown[]>('GET', '/admin/news', undefined, true),
  createNews: (data: unknown) => request<unknown>('POST', '/admin/news', data, true),
  updateNews: (id: string, data: unknown) => request<unknown>('PUT', `/admin/news/${id}`, data, true),
  deleteNews: (id: string) => request<unknown>('DELETE', `/admin/news/${id}`, undefined, true),

  // FAQ
  getFAQ: () => request<unknown[]>('GET', '/admin/faq', undefined, true),
  createFAQ: (data: unknown) => request<unknown>('POST', '/admin/faq', data, true),
  updateFAQ: (id: string, data: unknown) => request<unknown>('PUT', `/admin/faq/${id}`, data, true),
  deleteFAQ: (id: string) => request<unknown>('DELETE', `/admin/faq/${id}`, undefined, true),
  reorderFAQ: (orderedIds: string[]) => request<unknown[]>('PUT', '/admin/faq/reorder', { orderedIds }, true),

  // Partners
  getPartners: () => request<unknown[]>('GET', '/admin/partners', undefined, true),
  createPartner: (data: unknown) => request<unknown>('POST', '/admin/partners', data, true),
  updatePartner: (id: string, data: unknown) => request<unknown>('PUT', `/admin/partners/${id}`, data, true),
  deletePartner: (id: string) => request<unknown>('DELETE', `/admin/partners/${id}`, undefined, true),
  reorderPartners: (orderedIds: string[]) => request<unknown[]>('PUT', '/admin/partners/reorder', { orderedIds }, true),

  // Settings
  getSettings: () => request<unknown>('GET', '/admin/settings', undefined, true),
  updateSettings: (data: unknown) => request<unknown>('PUT', '/admin/settings', data, true),

  // Reviews
  getReviews: () => request<unknown[]>('GET', '/admin/reviews', undefined, true),
  createReview: (data: unknown) => request<unknown>('POST', '/admin/reviews', data, true),
  updateReview: (id: string, data: unknown) => request<unknown>('PUT', `/admin/reviews/${id}`, data, true),
  deleteReview: (id: string) => request<unknown>('DELETE', `/admin/reviews/${id}`, undefined, true),
  toggleReviewVisibility: (id: string) => request<unknown>('PUT', `/admin/reviews/${id}/toggle-visibility`, undefined, true),

  // Orders
  getOrders: () => request<unknown[]>('GET', '/admin/orders', undefined, true),
  updateOrderStatus: (id: string, status: string) => request<unknown>('PUT', `/admin/orders/${id}/status`, { status }, true),
  updateOrderTracking: (id: string, trackingId: string, trackingUrl: string) => request<unknown>('PUT', `/admin/orders/${id}/tracking`, { trackingId, trackingUrl }, true),

  // Conversations
  getConversations: () => request<unknown[]>('GET', '/admin/conversations', undefined, true),
  addMessage: (id: string, data: unknown) => request<unknown>('POST', `/admin/conversations/${id}/messages`, data, true),
  resolveConversation: (id: string) => request<unknown>('PUT', `/admin/conversations/${id}/resolve`, undefined, true),
  reopenConversation: (id: string) => request<unknown>('PUT', `/admin/conversations/${id}/reopen`, undefined, true),
  markRead: (id: string) => request<unknown>('PUT', `/admin/conversations/${id}/read`, undefined, true),

  // Subscribers
  getSubscribers: () => request<unknown[]>('GET', '/admin/subscribers', undefined, true),
  removeSubscriber: (id: string) => request<unknown>('DELETE', `/admin/subscribers/${id}`, undefined, true),

  // Activity Log
  getActivityLog: () => request<unknown[]>('GET', '/admin/activity-log', undefined, true),

  // Payments
  refundOrder: (orderId: string, amount: string, reason: string) => request<unknown>('POST', `/payments/refund/${orderId}`, { amount, reason }, true),
  getPaymentEvents: (orderId: string) => request<unknown[]>('GET', `/payments/events/${orderId}`, undefined, true),

  // Users
  getUsers: () => request<unknown[]>('GET', '/admin/users', undefined, true),

  // Seed
  seed: (data: unknown) => request<unknown>('POST', '/admin/seed', data, true),
};

// Public write endpoints
export const publicWriteApi = {
  addSubscriber: (email: string) => request<unknown>('POST', '/subscribers', { email }),
  createConversation: (data: unknown) => request<unknown>('POST', '/conversations', data),
  addMessage: (id: string, data: unknown) => request<unknown>('POST', `/conversations/${id}/messages`, data),
  createOrder: (data: unknown) => request<unknown>('POST', '/orders', data),
};

// Auth API — user authentication with JWT
function authRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('gallery-auth-token');
  if (token) headers['Authorization'] = `Bearer ${token}`;

  return fetch(`${API_URL}/auth${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  }).then(async (res) => {
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: { code: 'UNKNOWN', message: res.statusText } }));
      throw new ApiError(res.status, err.error?.code || 'UNKNOWN', err.error?.message || res.statusText);
    }
    const json = await res.json();
    return json.data ?? json;
  });
}

// Payment API — buyer-facing payment endpoints
export const paymentApi = {
  createCheckout: (data: { orderId: string; currency: string; language?: string; saveCard?: boolean; idempotencyKey: string }) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('gallery-auth-token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(`${API_URL}/payments/create-checkout`, { method: 'POST', headers, body: JSON.stringify(data) })
      .then(async (res) => { const json = await res.json(); if (!res.ok) throw new ApiError(res.status, json.error?.code || 'ERROR', json.error?.message || 'Payment failed'); return json.data as { checkoutUrl: string; orderId: string }; });
  },
  getStatus: (orderId: string) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('gallery-auth-token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(`${API_URL}/payments/status/${orderId}`, { headers })
      .then(async (res) => {
        if (!res.ok) throw new ApiError(res.status, 'STATUS_ERROR', 'Failed to get payment status');
        const json = await res.json();
        return json.data as { orderId: string; paymentStatus: string; orderStatus: string; amount: number; currency: string };
      });
  },
};

export const authApi = {
  register: (data: { firstName: string; lastName: string; email: string; phone?: string; birthDate?: string; password: string }) =>
    authRequest<{ user: unknown; token: string }>('POST', '/register', data),
  login: (email: string, password: string) =>
    authRequest<{ user: unknown; token: string }>('POST', '/login', { email, password }),
  getMe: () => authRequest<unknown>('GET', '/me'),
  updateProfile: (data: unknown) => authRequest<unknown>('PUT', '/profile', data),
  changePassword: (currentPassword: string, newPassword: string) =>
    authRequest<unknown>('PUT', '/change-password', { currentPassword, newPassword }),
  toggleWishlist: (artworkId: string) => authRequest<{ wishlist: string[] }>('PUT', '/wishlist', { artworkId }),
  getMyConversations: () => authRequest<unknown[]>('GET', '/my-conversations'),
  getMyOrders: () => authRequest<unknown[]>('GET', '/my-orders'),
};
